/**
 * 组织方数据大屏（可视化②）：演示级聚合视图。
 * - 数据全部来自 GET /events/:code/screen：由人派生的桶 n<5 一律 "<5"（flows 为 count=null）；
 * - 前端不做任何换算之外的统计：宽度比例只是画图，数字原样展示；
 * - 被 k-匿名隐藏的流向画成虚线细线——「隐私保护实时可见」是演示素材的一部分。
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArcElement, Chart as ChartJS, DoughnutController, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

import { eventsApi } from '../api/events.js';
import type { EventScreenDto } from '../api/types.js';
import { ErrorState } from '../components/ErrorState.js';
import { Loading } from '../components/Loading.js';
import { Tag } from '../components/Tag.js';
import { formatDateTime } from '../lib/format.js';

ChartJS.register(DoughnutController, ArcElement, Tooltip);

/** 七校配色（桑基/环图共用，顺序与 SCHOOLS 无关，按出现顺序分配）。 */
const PALETTE = ['#0e7490', '#4f46e5', '#0f766e', '#b45309', '#be185d', '#4d7c0f', '#b91c1c'];

const TICK_FONT = { size: 14 };

/** 披露值参与计算，"<5" 只画最小占位条——不做任何反推。 */
function disclosed(value: number | '<5'): number | null {
  return typeof value === 'number' ? value : null;
}

function useClock(): string {
  const [text, setText] = useState(() => formatDateTime(new Date().toISOString()));
  useEffect(() => {
    const timer = setInterval(() => setText(formatDateTime(new Date().toISOString())), 1000);
    return () => clearInterval(timer);
  }, []);
  return text;
}

/** 横向条形（CSS 实现，数值原样展示，宽度仅按可披露值的比例画图）。 */
function BarList({
  items,
  valueOf,
  labelOf,
  unit,
  tone,
}: {
  items: { key: string; raw: number | '<5' }[];
  valueOf: (item: { raw: number | '<5' }) => number | '<5';
  labelOf: (item: { key: string }) => string;
  unit: string;
  tone: 'cyan' | 'violet';
}) {
  const values = items.map((item) => disclosed(valueOf(item))).filter((v): v is number => v !== null);
  const max = values.reduce((m, v) => Math.max(m, v), 0);
  return (
    <div className="screen-bars">
      {items.map((item) => {
        const value = valueOf(item);
        const num = disclosed(value);
        const width = num === null || max === 0 ? 5 : Math.max(5, Math.round((num / max) * 100));
        return (
          <div key={item.key} className="screen-bar-row">
            <span className="screen-bar-label">{labelOf(item)}</span>
            <div className="screen-bar-track">
              <div
                className={`screen-bar-fill ${num === null ? 'screen-bar-hidden' : ''} ${tone === 'violet' ? 'screen-bar-violet' : ''}`}
                style={{ width: `${width}%` }}
              />
            </div>
            <span className="screen-bar-value">{value}</span>
            <span className="screen-bar-unit">{unit}</span>
          </div>
        );
      })}
    </div>
  );
}

/** 转化漏斗：五段横向条，"<5" 用斜纹占位（不反推、不补零）。 */
function Funnel({ funnel }: { funnel: EventScreenDto['funnel'] }) {
  const values = funnel.map((stage) => disclosed(stage.count));
  const max = values.reduce<number>((m, v) => Math.max(m, v ?? 0), 0);
  return (
    <div className="screen-funnel">
      {funnel.map((stage, index) => {
        const num = disclosed(stage.count);
        const width = num === null || max === 0 ? 6 : Math.max(6, Math.round((num / max) * 100));
        return (
          <div key={stage.stage} className="screen-funnel-row">
            <span className="screen-funnel-label">
              <span className="screen-funnel-index">{index + 1}</span>
              {stage.label}
            </span>
            <div className="screen-bar-track">
              <div
                className={`screen-bar-fill ${num === null ? 'screen-bar-hidden' : ''}`}
                style={{ width: `${width}%` }}
              />
            </div>
            <span className="screen-bar-value">{stage.count}</span>
          </div>
        );
      })}
    </div>
  );
}

/** 跨校流动桑基（手写 SVG）：count=null 的流画成虚线细线（k-匿名隐藏中）。 */
function FlowSankey({ flows }: { flows: EventScreenDto['flows'] }) {
  const HEIGHT = 340;
  const LEFT_X = 120;
  const RIGHT_X = 520;

  const { leftNodes, rightNodes, links } = useMemo(() => {
    const leftTotals = new Map<string, { label: string; total: number }>();
    const rightTotals = new Map<string, { label: string; total: number }>();
    const maxDisclosed = flows.reduce((m, flow) => Math.max(m, flow.count ?? 0), 0);
    for (const flow of flows) {
      const weight = flow.count ?? 1; // 隐藏流按 1 参与布局，视觉上细一线
      const left = leftTotals.get(flow.from) ?? { label: flow.fromLabel, total: 0 };
      left.total += weight;
      leftTotals.set(flow.from, left);
      const right = rightTotals.get(flow.to) ?? { label: flow.toLabel, total: 0 };
      right.total += weight;
      rightTotals.set(flow.to, right);
    }
    const totalWeight = [...leftTotals.values()].reduce((s, node) => s + node.total, 0);
    const usable = HEIGHT - 12;
    const scale = totalWeight > 0 ? usable / totalWeight : 0;

    let leftY = 6;
    const leftNodes = [...leftTotals.entries()].map(([school, node], index) => {
      const height = Math.max(18, node.total * scale);
      const rendered = { school, label: node.label, y: leftY, height, used: 0, color: PALETTE[index % PALETTE.length] ?? '#0e7490' };
      leftY += height + 8;
      return rendered;
    });
    let rightY = 6;
    const rightNodes = [...rightTotals.entries()].map(([school, node]) => {
      const height = Math.max(18, node.total * scale);
      const rendered = { school, label: node.label, y: rightY, height, used: 0 };
      rightY += height + 8;
      return rendered;
    });

    const leftOf = new Map(leftNodes.map((node) => [node.school, node]));
    const rightOf = new Map(rightNodes.map((node) => [node.school, node]));
    const links = flows.flatMap((flow) => {
      const left = leftOf.get(flow.from);
      const right = rightOf.get(flow.to);
      if (left === undefined || right === undefined) return [];
      const count = flow.count;
      const hidden = count === null;
      const weight = count ?? 1;
      const strokeWidth = count === null ? 1.5 : Math.max(2, Math.round((count / Math.max(1, maxDisclosed)) * 20));
      const y1 = left.y + left.used + strokeWidth / 2;
      left.used += Math.max(3, weight * scale * 0.6);
      const y2 = right.y + right.used + strokeWidth / 2;
      right.used += Math.max(3, weight * scale * 0.6);
      return [{ ...flow, hidden, y1, y2, strokeWidth, color: left.color }];
    });
    return { leftNodes, rightNodes, links };
  }, [flows]);

  const width = RIGHT_X + 150;
  return (
    <div className="screen-sankey-wrap">
      <svg viewBox={`0 0 ${width} ${HEIGHT}`} role="img" aria-label="跨校组队流向图（虚线为 k-匿名隐藏的流）">
        {links.map((link) => (
          <path
            key={`${link.from}-${link.to}`}
            d={`M ${LEFT_X} ${link.y1} C ${LEFT_X + 130} ${link.y1}, ${RIGHT_X - 130} ${link.y2}, ${RIGHT_X} ${link.y2}`}
            fill="none"
            stroke={link.hidden ? '#5b6b85' : link.color}
            strokeWidth={link.strokeWidth}
            strokeDasharray={link.hidden ? '4 4' : undefined}
            opacity={link.hidden ? 0.7 : 0.45}
          />
        ))}
        {leftNodes.map((node) => (
          <g key={`l-${node.school}`}>
            <rect x={LEFT_X - 6} y={node.y} width={6} height={node.height} rx={3} fill={node.color} />
            <text x={LEFT_X - 14} y={node.y + node.height / 2 + 4} textAnchor="end" className="screen-sankey-text">
              {node.label}
            </text>
          </g>
        ))}
        {rightNodes.map((node) => (
          <g key={`r-${node.school}`}>
            <rect x={RIGHT_X} y={node.y} width={6} height={node.height} rx={3} fill="#0f766e" />
            <text x={RIGHT_X + 14} y={node.y + node.height / 2 + 4} className="screen-sankey-text">
              {node.label}
            </text>
          </g>
        ))}
      </svg>
      <p className="screen-hint">虚线 = 不足 {5} 人的校际流动，已按 k-匿名隐藏精确计数</p>
    </div>
  );
}

export function EventScreenPage() {
  const { code = '' } = useParams();
  const clock = useClock();
  const screenQuery = useQuery({
    queryKey: ['event-screen', code],
    queryFn: () => eventsApi.screen(code),
    refetchInterval: 60_000,
  });

  if (screenQuery.isPending) {
    return <Loading label="正在计算数据大屏…" rows={4} />;
  }
  if (screenQuery.isError || screenQuery.data === undefined) {
    return (
      <div className="stack">
        <ErrorState error={screenQuery.error} title="读取数据大屏失败" onRetry={() => void screenQuery.refetch()} />
        <Link to={`/events/${code}`} className="btn btn-ghost">
          返回赛事详情
        </Link>
      </div>
    );
  }

  const screen = screenQuery.data;
  const doughnutData = {
    labels: ['已成队', '组建中'],
    datasets: [
      {
        data: [screen.teams.confirmed, screen.teams.forming],
        backgroundColor: ['#0e7490', '#4f46e5'],
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="screen-shell">
      <header className="screen-header">
        <div>
          <h1 className="screen-title">{screen.eventName}</h1>
          <p className="screen-subtitle">
            {screen.season} · 数据大屏 · 更新于 {clock}
          </p>
        </div>
        <div className="row-tight">
          <Tag tone="primary">k-匿名保护中（n&lt;{screen.kAnon} 不披露）</Tag>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void screenQuery.refetch()}>
            刷新
          </button>
          <Link to={`/events/${code}`} className="btn btn-ghost btn-sm">
            返回
          </Link>
        </div>
      </header>

      <section className="screen-kpis" aria-label="核心指标">
        <div className="screen-kpi">
          <span className="screen-kpi-value">{screen.teams.total}</span>
          <span className="screen-kpi-label">
            队伍总数（已成队 {screen.teams.confirmed} · 组建中 {screen.teams.forming}）
          </span>
        </div>
        <div className="screen-kpi">
          <span className="screen-kpi-value">{screen.groupedPeople}</span>
          <span className="screen-kpi-label">已组队人数（跨队去重）</span>
        </div>
        <div className="screen-kpi">
          <span className="screen-kpi-value">{screen.avgFormingHours === null ? '—' : `${screen.avgFormingHours} h`}</span>
          <span className="screen-kpi-label">平均成队耗时</span>
        </div>
        <div className="screen-kpi">
          <span className="screen-kpi-value">{screen.candidateSupply}</span>
          <span className="screen-kpi-label">候选池供给（条目）</span>
        </div>
      </section>

      <div className="screen-grid">
        <section className="screen-panel">
          <h2 className="screen-panel-title">组队转化漏斗</h2>
          <Funnel funnel={screen.funnel} />
        </section>

        <section className="screen-panel">
          <h2 className="screen-panel-title">各校已组队人数</h2>
          <BarList
            items={screen.schoolDist.map((item) => ({ key: item.school, raw: item.count }))}
            valueOf={(item) => item.raw}
            labelOf={(item) => screen.schoolDist.find((dist) => dist.school === item.key)?.label ?? item.key}
            unit="人"
            tone="cyan"
          />
        </section>

        <section className="screen-panel screen-panel-wide">
          <h2 className="screen-panel-title">跨校组队流向（队长学校 → 队员学校）</h2>
          <FlowSankey flows={screen.flows} />
        </section>

        <section className="screen-panel">
          <h2 className="screen-panel-title">队员技能热度 top {screen.skillHeat.length}</h2>
          <BarList
            items={screen.skillHeat.map((item) => ({ key: item.skill, raw: item.people }))}
            valueOf={(item) => item.raw}
            labelOf={(item) => screen.skillHeat.find((heat) => heat.skill === item.key)?.label ?? item.key}
            unit="人"
            tone="violet"
          />
        </section>

        <section className="screen-panel">
          <h2 className="screen-panel-title">能力缺口榜（缺该技能的队伍数）</h2>
          <BarList
            items={screen.skillGaps.map((item) => ({ key: item.skill, raw: item.teamsMissing }))}
            valueOf={(item) => item.raw}
            labelOf={(item) => screen.skillGaps.find((gap) => gap.skill === item.key)?.label ?? item.key}
            unit="支队缺"
            tone="cyan"
          />
        </section>

        <section className="screen-panel">
          <h2 className="screen-panel-title">队伍状态</h2>
          <div className="screen-doughnut">
            <Doughnut
              data={doughnutData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                cutout: '62%',
                plugins: {
                  legend: { position: 'bottom', labels: { color: '#334155', font: TICK_FONT } },
                  tooltip: { titleFont: TICK_FONT, bodyFont: TICK_FONT },
                },
              }}
            />
          </div>
        </section>
      </div>

      <footer className="screen-interpretation">
        <div className="row-tight">
          <Tag tone={screen.interpretation.via === 'llm' ? 'primary' : 'muted'}>
            AI 解读 · via {screen.interpretation.via === 'llm' ? 'LLM 润色' : '规则模板'}
          </Tag>
          <span className="muted small">数字由代码模板拼死，LLM 仅润色</span>
        </div>
        <p className="wrap-anywhere">{screen.interpretation.text}</p>
        <p className="screen-hint">只出计数不出明细 · 生成于 {formatDateTime(screen.generatedAt)} · 每分钟自动刷新</p>
      </footer>
    </div>
  );
}
